// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — Pollen Fading (LYBNT)
// Path: src/components/dashboard/pollen/PollenFading.jsx
//
// "The pollen is drying up. Go before it's gone."
// LYBNT donors: gave last fiscal year (boundaries from chapter_settings.fiscal_year_start_month)
// but no gift yet in the current fiscal year.
//
// Contacted donors show in muted text so users can focus
// on those who still need outreach.
//
// Fiscal year boundaries come from chapter_settings.fiscal_year_start_month.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Leaf, X, Info, Sprout, EyeOff } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

// Compute fiscal year boundaries from the chapter's fiscal start month (1-12)
const getFiscalYearDates = (startMonth) => {
  const safeStart = Math.min(Math.max(parseInt(startMonth, 10) || 1, 1), 12);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based

  const currentFyStartYear =
    currentMonth >= safeStart ? currentYear : currentYear - 1;
  const mm = String(safeStart).padStart(2, "0");
  const currentFyStart = `${currentFyStartYear}-${mm}-01`;

  const lastFyStartYear = currentFyStartYear - 1;
  const lastFyStart = `${lastFyStartYear}-${mm}-01`;

  // Last day of the month before safeStart in the year currentFyStartYear
  const endMonth = safeStart === 1 ? 12 : safeStart - 1;
  const endYear = safeStart === 1 ? lastFyStartYear : currentFyStartYear;
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  const lastFyEnd = `${endYear}-${String(endMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  return { currentFyStart, lastFyStart, lastFyEnd };
};

export const PollenFading = ({ onClose }) => {
  const { palette, navigate, dataVersion, members, cultivationQueue, currentUser, userProfile, chapterSettings } = useApp();
  const [lybntDonors, setLybntDonors] = useState([]);
  const [contactedIds, setContactedIds] = useState(new Set());
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [fyLabel, setFyLabel] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  // Seed dismissed from profile
  useEffect(() => {
    const saved = userProfile?.pollen_dismissed;
    console.log("[PollenFading] Seeding dismissed from profile:", saved);
    if (Array.isArray(saved) && saved.length > 0) setDismissedIds(new Set(saved));
  }, [userProfile]);

  const persistDismissed = async (newSet) => {
    const arr = [...newSet];
    setDismissedIds(newSet);
    const userId = currentUser?.id;
    console.log("[PollenFading] Persisting dismissed:", arr.length, "items, userId:", userId);
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .update({ pollen_dismissed: arr })
        .eq("id", userId)
        .select("id, pollen_dismissed");
      console.log("[PollenFading] Persist result:", { data, error, rowsReturned: data?.length });
    } catch (err) {
      console.error("[PollenFading] Persist exception:", err);
    }
  };

  const handleDismiss = (memberId) => {
    const updated = new Set(dismissedIds);
    updated.add(memberId);
    persistDismissed(updated);
  };

  const handleRestore = (memberId) => {
    const updated = new Set(dismissedIds);
    updated.delete(memberId);
    persistDismissed(updated);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { currentFyStart, lastFyStart, lastFyEnd } = getFiscalYearDates(chapterSettings?.fiscalYearStartMonth);
        setFyLabel(`FY${lastFyStart.substring(2, 4)}–${lastFyEnd.substring(2, 4)}`);

        // Paginated queries — Supabase caps at 1000 rows per request
        const PAGE_SIZE = 1000;

        // Helper: paginate a query and collect all rows
        const fetchAll = async (query) => {
          const allRows = [];
          let offset = 0;
          while (true) {
            const { data: page, error } = await query().range(offset, offset + PAGE_SIZE - 1);
            if (error || !page) break;
            allRows.push(...page);
            if (page.length < PAGE_SIZE) break;
            offset += PAGE_SIZE;
          }
          return allRows;
        };

        // Paginated queries require a stable .order() or Supabase can
        // return overlapping/missing rows across .range() calls.

        // Query A: Gifts in last fiscal year
        const lastFyGifts = await fetchAll(() =>
          supabase
            .from("gifts")
            .select("member_id, gift_date, amount")
            .gte("gift_date", lastFyStart)
            .lte("gift_date", lastFyEnd)
            .order("id", { ascending: true })
        );

        // Query B: Gifts in current fiscal year (just need member_ids)
        const currentFyGifts = await fetchAll(() =>
          supabase
            .from("gifts")
            .select("member_id")
            .gte("gift_date", currentFyStart)
            .order("id", { ascending: true })
        );

        // Query C: Contact logs since current FY start (who's been contacted?)
        const recentContacts = await fetchAll(() =>
          supabase
            .from("contact_logs")
            .select("member_id")
            .gte("log_date", currentFyStart)
            .order("id", { ascending: true })
        );

        setContactedIds(new Set(recentContacts.map((log) => log.member_id)));

        // Set of members who gave in current FY
        const hasCurrentFyGift = new Set(
          currentFyGifts.map((gift) => gift.member_id)
        );

        // Group last-FY gifts by member
        const lastFyByMember = {};
        lastFyGifts.forEach((gift) => {
          const memberId = gift.member_id;
          if (!lastFyByMember[memberId]) {
            lastFyByMember[memberId] = { total: 0, count: 0, lastDate: null };
          }
          lastFyByMember[memberId].total += Number(gift.amount) || 0;
          lastFyByMember[memberId].count += 1;
          if (!lastFyByMember[memberId].lastDate || gift.gift_date > lastFyByMember[memberId].lastDate) {
            lastFyByMember[memberId].lastDate = gift.gift_date;
          }
        });

        // LYBNT = gave last FY, no gift in current FY
        // Sort: uncontacted first (sorted by total desc), then contacted (sorted by total desc)
        const results = Object.keys(lastFyByMember)
          .filter((memberId) => !hasCurrentFyGift.has(memberId))
          .map((memberId) => {
            const record = lastFyByMember[memberId];
            return {
              memberId,
              lastFyTotal: record.total,
              lastFyCount: record.count,
              lastFyLastDate: record.lastDate,
            };
          })
          .sort((a, b) => b.lastFyTotal - a.lastFyTotal);

        setLybntDonors(results);
      } catch (err) {
        // silent
      }
    };
    load();
  }, [dataVersion, chapterSettings?.fiscalYearStartMonth]);

  const getDonorName = (donor) => {
    const member = members.find((m) => m.id === donor.memberId);
    if (!member) return "—";
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "—"
    );
  };

  const visibleDonors = lybntDonors.filter((d) => !dismissedIds.has(d.memberId));
  const dismissedDonors = lybntDonors.filter((d) => dismissedIds.has(d.memberId));
  const uncontactedCount = visibleDonors.filter((d) => !contactedIds.has(d.memberId)).length;

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Leaf size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            LYBNT
          </span>
          {lybntDonors.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {uncontactedCount} / {visibleDonors.length}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setShowInfo((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
              opacity: showInfo ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
            title="How this card works"
          >
            <Info size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {showInfo && (
        <div
          style={{
            padding: "10px 16px",
            fontSize: FONT_SIZES.xs,
            lineHeight: 1.5,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            background: `${palette.accent1}06`,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <strong>LYBNT</strong> — Last Year But Not This. These donors gave
          in {fyLabel} but haven't given yet this fiscal year. Names
          in <span style={{ color: palette.text3 }}>muted text</span> have
          already been contacted this year. The badge
          shows <strong>uncontacted / total</strong>.
        </div>
      )}

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 4px",
          fontSize: "0.65rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          fontStyle: "italic",
        }}
      >
        Gave in {fyLabel} but not yet this year. Go before it's gone.
      </div>

      {/* Donor list — scrollable */}
      {visibleDonors.length === 0 && dismissedDonors.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No LYBNT donors right now.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "4px 0",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {visibleDonors.map((donor) => {
            const contacted = contactedIds.has(donor.memberId);
            return (
              <div
                key={donor.memberId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 16px",
                  transition: "background 0.1s",
                  opacity: contacted ? 0.55 : 1,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = `${palette.accent1}08`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "transparent";
                }}
              >
                {/* Dismiss button */}
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDismiss(donor.memberId);
                  }}
                  title="No action needed"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 2,
                    display: "flex",
                    alignItems: "center",
                    opacity: 0.3,
                    transition: "opacity 0.15s",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(event) => { event.currentTarget.style.opacity = "0.8"; }}
                  onMouseLeave={(event) => { event.currentTarget.style.opacity = "0.3"; }}
                >
                  <EyeOff size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
                </button>

                {/* Name — clickable */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    cursor: members.find((m) => m.id === donor.memberId) ? "pointer" : "default",
                  }}
                  onClick={() => {
                    const fullMember = members.find((m) => m.id === donor.memberId);
                    if (fullMember)
                      navigate("memberDetail", fullMember, {
                        from: "lybnt",
                        suggestedTemplate: "lybnt",
                      });
                  }}
                >
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: contacted ? 400 : 600,
                      color: contacted ? palette.text3 : palette.text1,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textDecoration: contacted ? "line-through" : "none",
                    }}
                  >
                    {getDonorName(donor)}
                  </div>
                </div>
                {cultivationQueue.some((q) => q.memberId === donor.memberId) && (
                  <Sprout size={ICON_SIZES.xs} style={{ color: "#10b981", flexShrink: 0 }} title="In cultivation queue" />
                )}
                {contacted && (
                  <span
                    style={{
                      fontSize: "0.5rem",
                      fontWeight: 600,
                      padding: "1px 5px",
                      borderRadius: 4,
                      background: "#10b98115",
                      color: "#10b981",
                      fontFamily: FONT_FAMILY,
                      flexShrink: 0,
                    }}
                  >
                    contacted
                  </span>
                )}
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {donor.lastFyCount} gift{donor.lastFyCount > 1 ? "s" : ""}
                </span>
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: contacted ? palette.text3 : "#f59e0b",
                    fontFamily: FONT_FAMILY,
                    flexShrink: 0,
                  }}
                >
                  {fmtCurrency(donor.lastFyTotal)}
                </span>
              </div>
            );
          })}

          {/* Dismissed section */}
          {dismissedDonors.length > 0 && (
            <div
              style={{
                borderTop: `1px solid ${palette.cardBorder}`,
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              <button
                onClick={() => setShowDismissed((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  width: "100%",
                  padding: "6px 16px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <EyeOff size={ICON_SIZES.xs} />
                {dismissedDonors.length} dismissed
                <span style={{ marginLeft: 4 }}>{showDismissed ? "▾" : "▸"}</span>
              </button>
              {showDismissed && dismissedDonors.map((donor) => (
                <div
                  key={donor.memberId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 16px",
                    opacity: 0.4,
                  }}
                >
                  <button
                    onClick={() => handleRestore(donor.memberId)}
                    title="Restore to list"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <X size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
                  </button>
                  <span
                    style={{
                      flex: 1,
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getDonorName(donor)}
                  </span>
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      flexShrink: 0,
                    }}
                  >
                    {fmtCurrency(donor.lastFyTotal)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
